import hljs from "highlight.js/lib/core";
import xml from "highlight.js/lib/languages/xml";
import type { Plugin } from "../core/plugin";
import { PluginRegistry } from "../core/pluginRegistry";

hljs.registerLanguage("html", xml);

export class DocViewer implements Plugin {
  private currentVariantIndex: number = 0;

  private readonly tabButtons: NodeListOf<HTMLButtonElement>;
  private readonly panels: NodeListOf<HTMLElement>;

  private readonly variants: NodeListOf<HTMLElement>;
  private readonly prevBtn: HTMLButtonElement | null;
  private readonly nextBtn: HTMLButtonElement | null;
  private readonly label: HTMLButtonElement | null;
  private readonly variantList: HTMLElement | null;

  private readonly viewport: HTMLElement | null;
  private readonly resizeHandle: HTMLElement | null;
  private readonly widthDisplay: HTMLElement | null;
  private readonly htmlToggleBtns: NodeListOf<HTMLButtonElement>;
  private isResizing: boolean = false;
  private startX: number = 0;
  private startWidth: number = 0;

  constructor(private readonly element: HTMLElement) {
    this.tabButtons = element.querySelectorAll(".doc-viewer__tab-btn");
    this.panels = element.querySelectorAll(".doc-viewer__panel");

    this.variants = element.querySelectorAll(".doc-viewer__variant");
    this.prevBtn = element.querySelector(".doc-viewer__nav-btn--prev");
    this.nextBtn = element.querySelector(".doc-viewer__nav-btn--next");
    this.label = element.querySelector(".doc-viewer__variant-label");
    this.variantList = element.querySelector(".doc-viewer__variant-list");

    this.viewport = element.querySelector(".doc-viewer__viewport");
    this.resizeHandle = element.querySelector(".doc-viewer__resize-handle");
    this.widthDisplay = element.querySelector(".doc-viewer__width-display");
    this.htmlToggleBtns = element.querySelectorAll(
      ".doc-viewer__html-toggle-btn",
    );
  }
  destroy?(): void {
    throw new Error("Method not implemented.");
  }

  init(): void {
    this.setupTabs();
    this.setupCarousel();
    this.setupVariantSelector();
    this.setupResize();
    this.setupCopy();
    this.setupCodeToggles();
  }

  private setupVariantSelector(): void {
    if (!this.variantList || !this.label || this.variants.length === 0) return;

    this.variants.forEach((variant, index) => {
      const name = variant.dataset.variantName || `Exemplo ${index + 1}`;
      const item = document.createElement("button");
      item.className = "doc-viewer__variant-option";
      item.textContent = name;
      item.type = "button";
      item.setAttribute("role", "option");

      item.addEventListener("click", () => {
        this.currentVariantIndex = index;
        this.renderVariant();
        this.toggleVariantList(false);
      });

      this.variantList!.appendChild(item);
    });

    this.label.addEventListener("click", (e) => {
      e.stopPropagation();
      this.toggleVariantList();
    });

    document.addEventListener("click", (e) => {
      const target = e.target as Node;
      if (
        !this.label?.contains(target) &&
        !this.variantList?.contains(target)
      ) {
        this.toggleVariantList(false);
      }
    });
  }

  private toggleVariantList(show?: boolean): void {
    if (!this.variantList || !this.label) return;

    const isHidden = this.variantList.hidden;
    const shouldShow = show !== undefined ? show : isHidden;

    this.variantList.hidden = !shouldShow;
    this.label.setAttribute("aria-expanded", String(shouldShow));
  }

  private setupCopy(): void {
    const stylePanel = this.element.querySelector(
      '.doc-viewer__panel[data-panel="styles"] .doc-viewer__prose',
    );
    const copyBtn = this.element.querySelector(
      '.doc-viewer__panel[data-panel="styles"] .doc-viewer__copy-btn',
    );

    if (!copyBtn || !stylePanel) return;

    copyBtn.addEventListener("click", async () => {
      const text = stylePanel.textContent || "";

      try {
        await navigator.clipboard.writeText(text);
        const label = copyBtn.querySelector(".doc-viewer__copy-label");
        const originalText = label?.textContent;

        if (label) label.textContent = "Copied!";
        copyBtn.classList.add("doc-viewer__copy-btn--success");

        setTimeout(() => {
          if (label) label.textContent = originalText || "Copy";
          copyBtn.classList.remove("doc-viewer__copy-btn--success");
        }, 2000);
      } catch (err) {
        console.error("Failed to copy:", err);
      }
    });
  }

  private setupTabs(): void {
    // Restore tab from URL on load
    const initialTab = new URLSearchParams(location.search).get("tab");
    if (initialTab) {
      const btn = Array.from(this.tabButtons).find(
        (b) => b.dataset.tab === initialTab,
      );
      if (btn) this.switchTab(initialTab, btn, false);
    }

    this.tabButtons.forEach((btn) => {
      btn.addEventListener("click", () => {
        const targetTab = btn.dataset.tab;
        if (targetTab) this.switchTab(targetTab, btn, true);
      });
    });
  }

  private switchTab(
    tabName: string,
    activeBtn: HTMLButtonElement,
    pushToURL: boolean,
  ): void {
    this.tabButtons.forEach((btn) => {
      btn.classList.remove("doc-viewer__tab-btn--active");
      btn.setAttribute("aria-selected", "false");
    });
    activeBtn.classList.add("doc-viewer__tab-btn--active");
    activeBtn.setAttribute("aria-selected", "true");

    this.panels.forEach((panel) => {
      panel.classList.remove("doc-viewer__panel--active");
      panel.hidden = true;

      if (panel.dataset.panel === tabName) {
        panel.classList.add("doc-viewer__panel--active");
        panel.hidden = false;
      }
    });

    if (pushToURL) {
      const params = new URLSearchParams(location.search);
      if (tabName === "preview") {
        params.delete("tab");
      } else {
        params.set("tab", tabName);
      }
      const qs = params.toString();
      history.replaceState(null, "", qs ? `?${qs}` : location.pathname);
    }
  }

  private setupCarousel(): void {
    if (this.variants.length === 0) return;

    this.renderVariant();

    this.prevBtn?.addEventListener("click", () => {
      if (this.currentVariantIndex > 0) {
        this.currentVariantIndex--;
        this.renderVariant();
      }
    });

    this.nextBtn?.addEventListener("click", () => {
      if (this.currentVariantIndex < this.variants.length - 1) {
        this.currentVariantIndex++;
        this.renderVariant();
      }
    });
  }

  private renderVariant(): void {
    this.variants.forEach((variant, index) => {
      if (index === this.currentVariantIndex) {
        variant.classList.add("doc-viewer__variant--active");
      } else {
        variant.classList.remove("doc-viewer__variant--active");
      }
    });

    if (this.label && this.variants[this.currentVariantIndex]) {
      const name = this.variants[this.currentVariantIndex].dataset.variantName;
      const newText = name || `Exemplo ${this.currentVariantIndex + 1}`;

      // Update text node specifically to preserve SVG chevron
      let textNode = Array.from(this.label.childNodes).find(
        (node) =>
          node.nodeType === Node.TEXT_NODE &&
          (node.textContent?.trim().length ?? 0) > 0,
      );

      if (!textNode) {
        textNode = document.createTextNode(newText);
        this.label.prepend(textNode);
      } else {
        textNode.textContent = newText;
      }
    }

    if (this.variantList) {
      const options = this.variantList.querySelectorAll(
        ".doc-viewer__variant-option",
      );
      options.forEach((opt, idx) => {
        if (idx === this.currentVariantIndex) {
          opt.classList.add("doc-viewer__variant-option--active");
          opt.setAttribute("aria-selected", "true");
        } else {
          opt.classList.remove("doc-viewer__variant-option--active");
          opt.setAttribute("aria-selected", "false");
        }
      });
    }

    this.updateNavButtons();
    this.refreshOpenCodeBlocks();
  }

  private updateNavButtons(): void {
    if (this.prevBtn) {
      this.prevBtn.disabled = this.currentVariantIndex === 0;
    }
    if (this.nextBtn) {
      this.nextBtn.disabled =
        this.currentVariantIndex === this.variants.length - 1;
    }
  }

  private setupResize(): void {
    if (!this.resizeHandle || !this.viewport) return;

    this.resizeHandle.addEventListener("mousedown", (e) => {
      this.isResizing = true;
      this.startX = e.clientX;
      this.startWidth = this.viewport!.getBoundingClientRect().width;

      this.resizeHandle?.classList.add("doc-viewer__resize-handle--active");
      document.body.style.userSelect = "none";
      document.body.style.cursor = "col-resize";
    });

    window.addEventListener("mousemove", (e) => {
      if (!this.isResizing || !this.viewport) return;

      const dx = (e.clientX - this.startX) * 2;
      const newWidth = this.startWidth + dx;

      const parentWidth = this.element.getBoundingClientRect().width;

      if (newWidth > 320 && newWidth <= parentWidth) {
        this.viewport.style.width = `${newWidth}px`;
        this.updateWidthDisplay(newWidth);
      }
    });

    window.addEventListener("mouseup", () => {
      if (this.isResizing) {
        this.isResizing = false;
        this.resizeHandle?.classList.remove(
          "doc-viewer__resize-handle--active",
        );
        document.body.style.userSelect = "";
        document.body.style.cursor = "";
      }
    });
  }

  private updateWidthDisplay(width: number): void {
    if (this.widthDisplay) {
      this.widthDisplay.textContent = `${Math.round(width)}px`;
    }
  }

  private setupCodeToggles(): void {
    this.updateToggleVisibility();

    this.htmlToggleBtns.forEach((btn) => {
      const key = btn.dataset.toggle as "html" | "astro" | "react";
      if (!key) return;

      const block = this.element.querySelector(
        `.doc-viewer__html-block--${key}`,
      ) as HTMLElement | null;
      if (!block) return;

      btn.addEventListener("click", () => {
        const isOpen = !block.hidden;

        if (!isOpen) {
          // Close sibling blocks
          this.htmlToggleBtns.forEach((b) => {
            const k = b.dataset.toggle;
            if (k && k !== key) {
              const sibling = this.element.querySelector(
                `.doc-viewer__html-block--${k}`,
              ) as HTMLElement | null;
              if (sibling) sibling.hidden = true;
              b.setAttribute("aria-expanded", "false");
              b.classList.remove("doc-viewer__html-toggle-btn--active");
            }
          });
          this.updateCodeBlock(key);
        }

        block.hidden = isOpen;
        btn.setAttribute("aria-expanded", String(!isOpen));
        btn.classList.toggle("doc-viewer__html-toggle-btn--active", !isOpen);
      });

      const copyBtn = block.querySelector(
        ".doc-viewer__copy-btn",
      ) as HTMLButtonElement | null;
      if (!copyBtn) return;

      copyBtn.addEventListener("click", async () => {
        const text = block.querySelector("code")?.textContent ?? "";
        try {
          await navigator.clipboard.writeText(text);
          const label = copyBtn.querySelector(".doc-viewer__copy-label");
          const original = label?.textContent;
          if (label) label.textContent = "Copied!";
          copyBtn.classList.add("doc-viewer__copy-btn--success");
          setTimeout(() => {
            if (label) label.textContent = original ?? "Copy";
            copyBtn.classList.remove("doc-viewer__copy-btn--success");
          }, 2000);
        } catch {}
      });
    });
  }

  private refreshOpenCodeBlocks(): void {
    (["html", "astro", "react"] as const).forEach((key) => {
      const block = this.element.querySelector(
        `.doc-viewer__html-block--${key}`,
      ) as HTMLElement | null;
      if (block && !block.hidden) this.updateCodeBlock(key);
    });
    this.updateToggleVisibility();
  }

  // HTML is opt-out (visible by default; a variant can hide it via
  // `data-hide-html` when the auto-generated DOM dump adds no value —
  // e.g. a variant that mixes multiple framework ports in one section).
  // Astro/React are opt-in (hidden by default; shown only when the variant
  // supplies a `template[data-code-astro]` / `template[data-code-react]`).
  private updateToggleVisibility(): void {
    const variant =
      this.variants.length > 0 ? this.variants[this.currentVariantIndex] : null;

    const htmlBtn = Array.from(this.htmlToggleBtns).find(
      (b) => b.dataset.toggle === "html",
    );
    if (htmlBtn) {
      const hideHtml = Boolean(variant?.hasAttribute("data-hide-html"));
      this.setToggleVisibility(htmlBtn, "html", !hideHtml);
    }

    (["astro", "react"] as const).forEach((key) => {
      const btn = Array.from(this.htmlToggleBtns).find(
        (b) => b.dataset.toggle === key,
      );
      if (!btn) return;

      const hasTemplate = Boolean(
        variant?.querySelector(`template[data-code-${key}]`),
      );
      this.setToggleVisibility(btn, key, hasTemplate);
    });
  }

  private setToggleVisibility(
    btn: HTMLButtonElement,
    key: "html" | "astro" | "react",
    visible: boolean,
  ): void {
    btn.hidden = !visible;

    // If the toggle is hidden, close its block so it can't stay stuck open
    // when navigating to a variant that no longer supports it.
    if (!visible) {
      const block = this.element.querySelector(
        `.doc-viewer__html-block--${key}`,
      ) as HTMLElement | null;
      if (block) block.hidden = true;
      btn.setAttribute("aria-expanded", "false");
      btn.classList.remove("doc-viewer__html-toggle-btn--active");
    }
  }

  private updateCodeBlock(key: "html" | "astro" | "react"): void {
    const block = this.element.querySelector(
      `.doc-viewer__html-block--${key}`,
    ) as HTMLElement | null;
    const content = block?.querySelector(".doc-viewer__code-content");
    if (!block || !content) return;

    if (key === "html") {
      // Auto-generate from DOM
      const source =
        this.variants.length > 0
          ? this.variants[this.currentVariantIndex]
          : this.viewport;
      if (!source) return;

      const raw = this.extractCleanHTML(source);
      const highlighted = hljs.highlight(raw, { language: "html" }).value;
      content.innerHTML = `<pre class="doc-viewer__html-pre hljs"><code>${highlighted}</code></pre>`;
    } else {
      // Astro/React: from the variant's matching opt-in template
      const variant =
        this.variants.length > 0
          ? this.variants[this.currentVariantIndex]
          : (this.viewport ?? null);
      const template = (variant as HTMLElement | null)?.querySelector(
        `template[data-code-${key}]`,
      ) as HTMLTemplateElement | null;

      content.innerHTML = "";
      if (template) content.appendChild(template.content.cloneNode(true));
    }
  }

  private extractCleanHTML(source: HTMLElement): string {
    const clone = source.cloneNode(true) as HTMLElement;
    // Remove code templates — they're author metadata, not rendered output
    clone
      .querySelectorAll(
        "template[data-code-html], template[data-code-astro], template[data-code-react]",
      )
      .forEach((t) => t.remove());
    return this.formatHTML(clone.innerHTML.trim());
  }

  private formatHTML(html: string): string {
    let indent = 0;
    const tab = "  ";
    return html
      .replace(/>\s*</g, ">\n<")
      .split("\n")
      .map((line) => {
        line = line.trim();
        if (!line) return "";
        if (line.startsWith("</")) indent = Math.max(0, indent - 1);
        const result = tab.repeat(indent) + line;
        if (
          line.startsWith("<") &&
          !line.startsWith("</") &&
          !line.startsWith("<!") &&
          !line.endsWith("/>") &&
          !line.includes("</")
        )
          indent++;
        return result;
      })
      .filter(Boolean)
      .join("\n");
  }
}

PluginRegistry.register("doc-viewer", DocViewer);
